package com.downdogapp.client.views

import com.downdogapp.*
import com.downdogapp.AlignItems.CENTER
import com.downdogapp.AlignItems.FLEX_START
import com.downdogapp.Display.BLOCK
import com.downdogapp.Display.FLEX
import com.downdogapp.Display.NONE
import com.downdogapp.FlexDirection.COLUMN
import com.downdogapp.FlexDirection.ROW
import com.downdogapp.Position.FIXED
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.Size.Companion.vw
import com.downdogapp.TextDecorationLine.UNDERLINE
import com.downdogapp.client.AppPage
import com.downdogapp.client.FaqItem.CANT_ACCESS_SUBSCRIPTION
import com.downdogapp.client.MANIFEST
import com.downdogapp.client.Strings
import com.downdogapp.client.api.FeedbackType
import com.downdogapp.client.api.FeedbackType.*
import com.downdogapp.client.controllers.SupportViewController
import com.downdogapp.client.controllers.SupportViewController.Companion.getRelevantFAQs
import com.downdogapp.client.resources.Images
import com.downdogapp.client.resources.image
import com.downdogapp.client.singleton.App
import com.downdogapp.client.widget.ScreenWidth.*
import com.downdogapp.client.widget.TopLevelView
import com.downdogapp.client.widget.controllerView
import com.downdogapp.client.widget.screenWidthDependent
import kotlinx.dom.clear
import org.w3c.dom.*

class SupportView(private val controller: SupportViewController) : TopLevelView() {

  private var pageContent: HTMLDivElement

  override var root: HTMLElement =
    controllerView {
      bgColor = Color.WHITE
      fontFamily = "Montserrat"
      fontWeight = FontWeight.REGULAR
      margin = px(0)
      txtColor = Color(51, 51, 51)
      screenWidthDependent { width ->
        fontSize = if (width == PHONE) px(18) else px(15)
      }
      div {
        display = FLEX
        overflowX = Overflow.HIDDEN
        overflowY = Overflow.HIDDEN
        paddingBottom = px(10)
        div { // left Col
          bgColor = Color.WHITE
          screenWidthDependent { widths ->
            when (widths) {
              LARGE -> {
                width = vw(50) - px(160)
                paddingTop = px(60)
                paddingBottom = px(60)
                paddingLeft = px(80)
                paddingRight = px(80)
              }

              MEDIUM, SMALL -> {
                width = percent(100) - px(160)
                paddingTop = px(60)
                paddingBottom = px(60)
                paddingLeft = px(80)
                paddingRight = px(80)
              }

              PHONE -> {
                width = percent(100) - px(40)
                paddingTop = px(30)
                paddingBottom = px(30)
                paddingLeft = px(20)
                paddingRight = px(20)
              }
            }
          }

          a {
            href = "/"
            target = ""
            screenWidthDependent { width ->
              if (width == PHONE) {
                display = FLEX
                alignItems = CENTER
                justifyContent = JustifyContent.CENTER
              } else {
                alignItems = FLEX_START
                justifyContent = JustifyContent.FLEX_START
              }
            }
            img {
              width = 100
              height = 18
              margin = px(0)
              image = Images.BLUE_LOGO
            }
          }
          pageContent = div()
        }
        div {
          position = FIXED
          right = px(0)
          maxWidth = vw(50)
          screenWidthDependent { width ->
            display = if (width == LARGE) FLEX else NONE
          }
          img {
            style.height = "100vh"
            image = Images.SUPPORT_HERO
          }
        }
      }
    }

  init {
    showImportantFaqs()
  }

  private fun refresh(block: HTMLElement.() -> Unit) =
    pageContent.apply {
      clear()
      this.block()
    }

  private fun feedbackTypeSelected(feedbackType: FeedbackType?) {
    when (feedbackType) {
      BILLING, ACCOUNT_AND_LOGIN_ISSUES -> {
        refresh {
          feedbackTypeSelect(default = feedbackType)
          subscriptionsContent()

          if (MANIFEST.subscriptions.isNotEmpty()) {
            refundRequestContent()
          }

          feedbackContent(feedbackType)
        }
      }

      AFFORDABILITY -> {
        refresh {
          feedbackTypeSelect(default = AFFORDABILITY)
          infoText(Strings.LOOK_SCHOOL_PAGE("<a href=\"/school\">${Strings.SCHOOL_MEMBERSHIPS}</a>"))
          infoText(Strings.AFFORDABILITY_MESSAGE)
          emailContent(Strings.AFFORDABILITY_FEEDBACK_PROMPT + ":", feedbackType)
        }
      }

      FREE_FOR_SCHOOLS -> {
        showOrganizationsText(
          policyText = Strings.STUDENTS_FREE_UNTIL_JANUARY_1_LONG,
          getMembershipText = Strings.GET_STUDENT_MEMBERSHIP,
          buttonUrl = "/schools",
          feedbackType = feedbackType
        )
      }

      PRACTICE_FEEDBACK, EXERCISE_ADVICE -> {
        refresh {
          feedbackTypeSelect(default = feedbackType)
          feedbackContent(feedbackType, linkToFacebook = true)
        }
      }

      OTHER -> refresh {
        feedbackTypeSelect(default = OTHER)
        infoText(Strings.CHECK_OUT_QUESTION("<a href=\"/faq\">${Strings.FAQ_PAGE}</a>"))
        emailContent(feedbackType = feedbackType)
      }

      TECH_ISSUES, FEATURE_REQUEST -> {
        refresh {
          feedbackTypeSelect(default = feedbackType)
          feedbackContent(feedbackType)
        }
      }

      else -> {
        showImportantFaqs()
      }
    }
  }

  private fun HTMLElement.feedbackTypeSelect(default: FeedbackType?) =
    div {
      marginBottom = px(25)
      screenWidthDependent { width ->
        marginTop = if (width == PHONE) px(20) else px(50)
      }
      div {
        flexDirection = COLUMN
        p {
          textContent = Strings.I_HAVE_A_QUESTION_ABOUT
          marginBottom = px(4)
          fontWeight = FontWeight.SEMIBOLD
          fontSize = px(18)
        }
        div {
          className = "select-wrapper"
          position = RELATIVE
          select {
            paddingHorizontal = px(20)
            paddingVertical = px(10)
            border = Border.NONE
            borderRadius = px(6)
            bgColor = Color.GREY
            display = FLEX
            overflowY = Overflow.HIDDEN
            overflowX = Overflow.HIDDEN
            appearance = Appearance.NONE
            width = percent(100)
            screenWidthDependent { width ->
              if (width == PHONE) {
                height = px(60)
                fontSize = px(18)
              } else {
                height = px(40)
                fontSize = px(16)
              }
            }

            option {
              value = ""
            }

            FeedbackType
              .entries
              .map { ft ->
                option {
                  textContent = when (ft) {
                    ACCOUNT_AND_LOGIN_ISSUES -> Strings.ACCOUNT
                    BILLING -> Strings.BILLING
                    FREE_FOR_SCHOOLS -> Strings.FREE_FOR_SCHOOLS
                    TECH_ISSUES -> Strings.TECH_ISSUES
                    FEATURE_REQUEST -> Strings.FEATURE_REQUEST
                    EXERCISE_ADVICE -> Strings.EXERCISE_ADVICE
                    PRACTICE_FEEDBACK -> Strings.PRACTICE_FEEDBACK
                    AFFORDABILITY -> Strings.AFFORDABILITY_SUPPORT_PAGE_TITLE
                    OTHER -> Strings.OTHER
                  }
                  value = ft.name
                  this.selected = ft == default
                }
              }

            onchange = {
              feedbackTypeSelected(
                feedbackType = if (value.isNotEmpty()) FeedbackType.valueOf(value) else null
              )
            }
          }
        }
      }
    }

  private fun HTMLElement.doneContent(message: String = Strings.FEEDBACK_SUBMITTED_TEXT) =
    div {
      marginTop = px(50)
      h3 {
        textContent = Strings.THANKS
      }
      span {
        textContent = message
      }
      br()
      span {
        textContent = Strings.SUPPORT_MANAGE_EXPECTATIONS_TEXT
      }
    }

  private fun HTMLElement.emailContent(prompt: String = Strings.WHATS_UP, feedbackType: FeedbackType) =
    div {
      val feedbackBox: HTMLTextAreaElement
      val feedbackBoxError: HTMLSpanElement
      div {
        display = FLEX

        screenWidthDependent { width ->
          marginBottom = if (width == PHONE) px(0) else px(10)
        }

        div {
          display = FLEX
          flexDirection = COLUMN
          width = percent(100)

          p {
            marginBottom = px(4)
            fontWeight = FontWeight.SEMIBOLD

            textContent = Strings.SUPPORT_MANAGE_EXPECTATIONS_TEXT
          }
        }
      }
      div {
        marginBottom = px(10)
        display = FLEX
        flexDirection = COLUMN
        width = percent(100)

        screenWidthDependent { width ->
          marginTop = if (width == PHONE) px(15) else px(0)
        }
        p {
          marginBottom = px(4)
          fontWeight = FontWeight.SEMIBOLD

          textContent = prompt
        }
        feedbackBox = textArea {
          padding = px(10)
          height = px(200)
          borderRadius = px(6)
          bgColor = Color.GREY
          border = Border.solid(px(1), Color.GREY)
          fontSize = px(16)
        }
        feedbackBoxError = span {
          txtColor = Color(255, 0, 0)
          display = NONE

          textContent = Strings.THIS_FORM_FIELD_IS_REQUIRED
        }
      }
      div {
        input {
          type = "submit"
          paddingLeft = px(15)
          paddingRight = px(15)
          paddingTop = px(5)
          paddingBottom = px(5)
          borderRadius = px(6)
          txtColor = Color.WHITE
          fontWeight = FontWeight.BOLD
          marginTop = px(10)
          screenWidthDependent { width ->
            if (width == PHONE) {
              fontSize = px(18)
              height = px(60)
            } else {
              fontSize = px(16)
              height = px(40)
            }
          }

          onClick {
            disabled = true
            value = Strings.SUBMITTING

            val feedbackContent =
              feedbackBox.value.ifBlank { null }

            if (feedbackContent.isNullOrEmpty()) {
              feedbackBoxError.show()
              disabled = false
              value = Strings.SUBMIT
            } else {
              controller.submitFeedback(
                feedbackType = feedbackType,
                feedbackContent = feedbackContent
              ) { success ->
                if (success) {
                  refresh {
                    doneContent()
                  }
                } else {
                  App.alert(message = Strings.SENDING_FEEDBACK_FAILED)
                  disabled = false
                  value = Strings.SUBMIT
                }
              }
            }
          }
        }
      }
    }

  private fun HTMLElement.infoText(innerHtml: String) = div {
    p {
      innerHTML = innerHtml
      fontWeight = FontWeight.MEDIUM
    }
  }

  private fun HTMLElement.infoTextAndButton(
    innerHtml: String,
    buttonText: String = Strings.OK,
    hideHelp: Boolean = false,
    feedbackType: FeedbackType = BILLING,
    buttonAction: () -> Unit = { showImportantFaqs() }
  ) =
    div {
      p {
        innerHTML = innerHtml
        fontWeight = FontWeight.MEDIUM
      }
      centerHorizontally {
        input {
          type = "submit"
          value = buttonText
          paddingLeft = px(15)
          paddingRight = px(15)
          paddingTop = px(5)
          paddingBottom = px(5)
          borderRadius = px(6)
          txtColor = Color.WHITE
          fontWeight = FontWeight.BOLD
          marginTop = px(10)
          screenWidthDependent { width ->
            if (width == PHONE) {
              fontSize = px(18)
              height = px(60)
            } else {
              fontSize = px(16)
              height = px(40)
            }
          }
          onClick {
            disabled = true
            value = Strings.SUBMITTING
            buttonAction()
          }
        }
        div {
          paddingTop = px(18)
          paddingBottom = px(18)
          fontSize = px(16)
          textContent = Strings.I_STILL_NEED_HELP
          fontWeight = FontWeight.SEMIBOLD
          txtColor = Color.DOWN_DOG_BLUE
          display = if (hideHelp) NONE else BLOCK
          onClick {
            refresh {
              backButtonContent(Strings.I_STILL_NEED_HELP)
              emailContent(feedbackType = feedbackType)
            }
          }
        }
      }
    }

  private fun HTMLElement.feedbackContent(feedbackType: FeedbackType, linkToFacebook: Boolean = false) =
    div {
      marginBottom = px(25)
      for ((index, faqItem) in getRelevantFAQs(feedbackType).withIndex()) {
        div {
          paddingTop = px(18)
          paddingBottom = px(18)
          borderTop = if (index == 0) Border.NONE else Border.solid(px(1), Color.GREY)
          fontSize = px(16)
          textContent = faqItem.question
          fontWeight = FontWeight.SEMIBOLD
          onClick {
            val answerContainer = this.nextElementSibling as HTMLDivElement
            if (answerContainer.display == NONE) answerContainer.show() else answerContainer.hide()
          }
        }
        div {
          display = NONE
          paddingLeft = px(18)
          paddingRight = px(18)
          p {
            fontSize = px(15)
            fontWeight = FontWeight.LIGHT
            innerHTML = faqItem.answer.replace("\n", "<br>")
          }
        }
      }
      val openEmailMessage = if (linkToFacebook) Strings.ASK_IN_FACEBOOK_GROUP else Strings.I_HAVE_A_DIFFERENT_QUESTION
      div {
        paddingTop = px(18)
        paddingBottom = px(18)
        borderTop = Border.solid(px(1), Color.GREY)
        fontSize = px(16)
        textContent = openEmailMessage
        fontWeight = FontWeight.SEMIBOLD
        txtColor = Color.DOWN_DOG_BLUE
        onClick {
          if (linkToFacebook) {
            MANIFEST.facebookCommunityUrl?.let { it1 -> AppPage.goTo(it1) }
          } else {
            refresh {
              backButtonContent(openEmailMessage)
              emailContent(feedbackType = feedbackType)
            }
          }
        }
      }
    }

  private fun HTMLElement.importantFAQs() =
    div {
      marginBottom = px(25)
      div {
        paddingTop = px(18)
        paddingBottom = px(18)
        borderTop = Border.NONE
        fontSize = px(16)
        textContent = CANT_ACCESS_SUBSCRIPTION.question
        fontWeight = FontWeight.SEMIBOLD
        onClick {
          val answerContainer = this.nextElementSibling as HTMLDivElement
          if (answerContainer.display == NONE) answerContainer.show() else answerContainer.hide()
        }
      }
      div {
        display = NONE
        paddingLeft = px(18)
        paddingRight = px(18)

        p {
          fontSize = px(15)
          fontWeight = FontWeight.LIGHT
          innerHTML = CANT_ACCESS_SUBSCRIPTION.answer.replace("\n", "<br>")
        }
      }
    }

  private fun HTMLElement.refundRequestContent(showChargedTwice: Boolean = true,
                                               extraMarginTop: Boolean = false): HTMLDivElement =
    div {
      div {
        paddingTop = if (extraMarginTop) px(50) else px(18)
        paddingBottom = px(18)
        borderBottom = Border.solid(px(1), Color.GREY)
        fontSize = px(16)
        textContent = Strings.I_WANT_A_REFUND
        fontWeight = FontWeight.SEMIBOLD
        onClick { showRefundInfo() }
      }
      if (showChargedTwice) {
        div {
          paddingTop = px(18)
          paddingBottom = px(18)
          borderBottom = Border.solid(px(1), Color.GREY)
          fontSize = px(16)
          textContent = Strings.CHARGED_TWICE
          fontWeight = FontWeight.SEMIBOLD
          onClick { showRefundInfo() }
        }
      }
    }

  private fun HTMLElement.subscriptionsContent() =
    div {
      marginBottom = px(25)
      padding = px(20)
      bgColor = Color.GREY
      borderRadius = px(6)

      val hasSubscription = MANIFEST.subscriptions.any { it.active && !it.trial }
      if (hasSubscription) {
        p {
          innerHTML = Strings.SUBSCRIPTIONS_SUPPORT_MESSAGE("<b>${MANIFEST.email}</b>")
        }
        MANIFEST.subscriptions.map { subscription ->
          if (!subscription.trial)
            div {
              display = FLEX
              flexDirection = ROW
              alignItems = CENTER
              padding = px(10)
              height = px(40)
              marginBottom = px(8)
              borderRadius = px(6)
              bgColor = Color.WHITE
              fontSize = px(16)
              div {
                p {
                  textContent = subscription.planButtonText
                  fontWeight = FontWeight.SEMIBOLD
                }
              }
              div {
                flexGrow = 1.0
              }
              div {
                p {
                  textContent = subscription.planButtonSubtext
                }
              }
            }
        }
      } else {
        p {
          innerHTML = Strings.LOGGED_IN_AS("<b>${MANIFEST.email!!}</b>") + Strings.NO_SUBSCRIPTIONS_SUPPORT_MESSAGE
          fontWeight = FontWeight.MEDIUM
        }
      }

      div {
        paddingTop = px(18)
        paddingBottom = px(18)
        borderTop = Border.NONE
        fontSize = px(14)
        textContent = Strings.DONT_SEE_SUBSCRIPTION
        fontWeight = FontWeight.MEDIUM
        textDecorationLine = UNDERLINE
        onClick {
          val answerContainer = this.nextElementSibling as HTMLDivElement
          if (answerContainer.display == NONE) answerContainer.show() else answerContainer.hide()
        }
      }
      div {
        display = NONE
        paddingLeft = px(18)
        paddingRight = px(18)
        p {
          fontSize = px(13)
          fontWeight = FontWeight.LIGHT
          innerHTML = CANT_ACCESS_SUBSCRIPTION.answer.replace("\n", "<br>")
        }
      }
    }

  private fun HTMLElement.purchaseContent(price: String, date: String, boughtWith: String) = div {
    marginBottom = px(25)
    p {
      innerHTML = Strings.LATEST_PAYMENT
    }
    div {
      display = FLEX
      flexDirection = ROW
      alignItems = CENTER
      borderRadius = px(6)
      bgColor = Color.GREY
      fontSize = px(16)
      div {
        margin = px(10)
        p {
          textContent = date
        }
      }
      div {
        margin = px(10)
        p {
          textContent = price
          fontWeight = FontWeight.SEMIBOLD
        }
      }
      div {
        flexGrow = 1.0
      }
      div {
        margin = px(10)
        p {
          textContent = boughtWith
        }
      }
    }
  }

  private fun HTMLElement.backButtonContent(title: String) =
    div {
      marginBottom = px(25)

      div {
        display = FLEX
        flexDirection = ROW
        alignItems = CENTER
        marginTop = px(40)
        marginLeft = px(-12)
        onClick {
          showImportantFaqs()
        }
        img {
          height = 35
          image = Images.LEFT_ARROW_GREY_ICON
        }
        p {
          fontSize = px(14)
          textContent = Strings.BACK
          txtColor = Color.DARK_GREY
        }
      }
      p {
        fontSize = px(16)
        textContent = title
        fontWeight = FontWeight.SEMIBOLD
        txtColor = Color.DOWN_DOG_BLUE
      }
    }

  private fun showImportantFaqs(feedbackType: FeedbackType? = null) = refresh {
    refundRequestContent(
      showChargedTwice = false,
      extraMarginTop = true
    )
    importantFAQs()
    feedbackTypeSelect(default = feedbackType)
  }

  private fun showOrganizationsText(policyText: String,
                                    getMembershipText: String,
                                    buttonUrl: String,
                                    feedbackType: FeedbackType) =
    refresh {
      feedbackTypeSelect(default = feedbackType)
      subscriptionsContent()
      infoTextAndButton(
        innerHtml = policyText,
        buttonText = if (MANIFEST.subscriptions.any { it.active }) Strings.LEARN_MORE else getMembershipText,
        feedbackType = feedbackType
      ) {
        AppPage.goTo(buttonUrl)
      }
    }

  private fun showRefundInfo() =
    controller.getRefundablePayment { latestPaymentResponse ->
      latestPaymentResponse?.let {
        refresh {
          backButtonContent(Strings.I_WANT_A_REFUND)
          subscriptionsContent()

          if (latestPaymentResponse.paymentId != null) {
            purchaseContent(
              price = latestPaymentResponse.price!!,
              date = latestPaymentResponse.date!!,
              boughtWith = latestPaymentResponse.boughtWith!!
            )
          }

          if (latestPaymentResponse.apple == true) {
            infoText(Strings.APPLE_REFUND_POLICY("<a href=\"${appleSupportUrl}\">${appleSupportUrl}</a>"))
          }

          when {
            latestPaymentResponse.paymentId == null -> {
              infoTextAndButton(innerHtml = Strings.DIDNT_PURCHASE_SUBSCRIPTION, hideHelp = true)
            }

            latestPaymentResponse.alreadyRefunded == true -> {
              infoTextAndButton(Strings.PAYMENT_ALREADY_REFUNDED, hideHelp = true)
            }

            latestPaymentResponse.apple == true -> {
              infoTextAndButton(
                innerHtml = Strings.CONTACT_APPLE_FOR_REFUND,
                buttonText = Strings.GO_TO_APPLE_SUPPORT,
                hideHelp = true
              ) {
                AppPage.goTo(appleSupportUrl)
              }
            }

            latestPaymentResponse.practicedSincePurchase == true || latestPaymentResponse.purchaseNotRecent == true -> {
              infoTextAndButton(
                innerHtml = if (latestPaymentResponse.practicedSincePurchase == true)
                  Strings.NO_REFUND_PRACTICED
                else
                  Strings.NO_REFUND_TIME(if (latestPaymentResponse.monthly == true) 7 else 30),
                hideHelp = true
              )
            }

            else -> {
              infoTextAndButton(Strings.WANT_REFUND_CONFIRMATION, Strings.YES_STRING, hideHelp = true) {
                controller.issueRefund(latestPaymentResponse.paymentId) { success ->
                  if (success) {
                    refresh {
                      doneContent(message = Strings.REFUNDED_ON_WAY)
                    }
                  } else {
                    App.alert(message = Strings.PAYMENT_ALREADY_REFUNDED)
                  }
                }
              }
            }
          }
        }
      }
    }

  companion object {

    private val appleSupportUrl = "https://support.apple.com/en-us/HT202039"
  }
}
